import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function MarketingSignup() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    phone: ''
  });
  const [subdomain, setSubdomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Auto-generate subdomain from company name
  useEffect(() => {
    if (formData.companyName) {
      const generated = formData.companyName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
        .substring(0, 30); // Limit length
      setSubdomain(generated);
    } else {
      setSubdomain('');
    }
  }, [formData.companyName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    // Validate subdomain
    if (!subdomain || subdomain.length < 3) {
      setError('Company name must generate a valid subdomain (at least 3 characters)');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/public/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          companyName: formData.companyName,
          phone: formData.phone,
          subdomain: subdomain
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="marketing-signup">
        <section className="marketing-hero marketing-hero-short">
          <div className="marketing-hero-content">
            <h1 className="marketing-hero-title">Check Your Email</h1>
            <p className="marketing-hero-subtitle">
              We've sent you a link to complete your setup.
            </p>
          </div>
        </section>

        <section className="marketing-signup-success">
          <div className="container">
            <div className="success-card">
              <div className="success-icon">&#9993;</div>
              <h2>Almost There!</h2>
              <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>
                We've sent a setup link to <strong>{formData.email}</strong>
              </p>

              <div className="success-steps">
                <div className="success-step">
                  <span className="step-number">1</span>
                  <div className="step-content">
                    <h4>Check your inbox</h4>
                    <p>Look for an email from DeedStack with the subject "Complete Your Setup"</p>
                  </div>
                </div>
                <div className="success-step">
                  <span className="step-number">2</span>
                  <div className="step-content">
                    <h4>Click the setup link</h4>
                    <p>The link will take you to create your password (expires in 24 hours)</p>
                  </div>
                </div>
                <div className="success-step">
                  <span className="step-number">3</span>
                  <div className="step-content">
                    <h4>Start your free trial</h4>
                    <p>Once your password is set, you'll have full access to your dashboard</p>
                  </div>
                </div>
              </div>

              <div style={{
                backgroundColor: 'var(--color-surface-sunken)',
                padding: '1rem 1.5rem',
                borderRadius: '8px',
                marginTop: '1.5rem',
                textAlign: 'left'
              }}>
                <p style={{ margin: 0, color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
                  <strong>Didn't receive the email?</strong><br />
                  Check your spam folder, or{' '}
                  <a href="https://deedstack.com/contact">contact us</a> for help.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="marketing-signup">
      {/* Hero */}
      <section className="marketing-hero marketing-hero-short">
        <div className="marketing-hero-content">
          <h1 className="marketing-hero-title">Start Your Free Trial</h1>
          <p className="marketing-hero-subtitle">
            14 days free. No credit card required. Get started in minutes.
          </p>
        </div>
      </section>

      {/* Signup Form */}
      <section className="marketing-signup-form-section">
        <div className="container">
          <div className="signup-grid">
            {/* Form Column */}
            <div className="signup-form-column">
              <div className="signup-form-card">
                <h2>Create Your Account</h2>

                {error && (
                  <div className="signup-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="signup-form">
                  <div className="form-group">
                    <label htmlFor="fullName">Full Name *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                    />
                    <span className="form-help">This will be your admin login</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="companyName">Company Name *</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      placeholder="Acme Land Company"
                    />
                    {subdomain && (
                      <div className="subdomain-preview">
                        Your site: <strong>{subdomain}.deedstack.com</strong>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone (optional)</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-large signup-submit"
                    disabled={submitting}
                  >
                    {submitting ? 'Creating Account...' : 'Start My Free Trial'}
                  </button>

                  <p className="signup-terms">
                    By signing up, you agree to our{' '}
                    <Link to="/terms">Terms of Service</Link> and{' '}
                    <Link to="/privacy">Privacy Policy</Link>.
                  </p>
                </form>
              </div>
            </div>

            {/* Benefits Column */}
            <div className="signup-benefits-column">
              <div className="benefits-card">
                <h3>What's Included in Your Free Trial</h3>
                <ul className="benefits-list">
                  <li>
                    <span className="benefit-check">✓</span>
                    <div>
                      <strong>3 Active Loans</strong>
                      <p>Manage up to 3 loans during your trial</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-check">✓</span>
                    <div>
                      <strong>10 Property Listings</strong>
                      <p>List properties for sale on your site</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-check">✓</span>
                    <div>
                      <strong>Customer Portal</strong>
                      <p>Self-service for your buyers</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-check">✓</span>
                    <div>
                      <strong>Sample Data Included</strong>
                      <p>See how DeedStack works with real examples</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-check">✓</span>
                    <div>
                      <strong>14 Days Free</strong>
                      <p>No credit card required to start</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="testimonial-card">
                <p className="testimonial-quote">
                  "DeedStack transformed how we manage our land contracts. Setup took 15 minutes and our buyers love the customer portal."
                </p>
                <p className="testimonial-author">
                  <strong>Mike R.</strong> - Texas Land Investor
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default MarketingSignup;
